import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import {
  computeFitScale, clampScale as clampScaleLib,
  fitAndCenter, isPlausibleViewport,
} from "@/lib/camera";
import {
  cameraReducer, initialCameraState,
} from "@/lib/cameraController";
import { readViewport, writeViewport } from "@/lib/viewportStore";
import TileLayer from "@/components/viewer/TileLayer";
import LayerOverlay from "@/components/viewer/LayerOverlay";

/**
 * SheetCanvas — map-like smooth pan/zoom canvas for a single sheet raster.
 *
 * The plans are the gravity. Everything in this component exists to make
 * the drawing feel immersive, physical, and instant on a tablet or trackpad.
 *
 * Implementation:
 *   - Renders ONE <img> inside a transformed wrapper.
 *   - CSS GPU-composited transform: translate(tx, ty) scale(s).
 *     No reflow, no repaint, no PDF.js worker churn — buttery smooth.
 *   - Unified Pointer Events for mouse + touch + pen + trackpad.
 *   - Pinch-zoom around the midpoint of two pointers.
 *   - Wheel-zoom around the cursor (or ctrl+wheel for trackpads).
 *   - Pin overlay is a sibling inside the same transformed wrapper so pins
 *     stay anchored to the drawing at any zoom/pan.
 *   - Viewport (tx, ty, s) is persisted per-sheet in localStorage.
 *
 * Props:
 *   sheetId            — for viewport persistence key
 *   rasterUrl          — backend-rendered JPEG of the sheet
 *   renderPin(p)       — render a pin marker at its (x,y)∈[0..1] normalized
 *   onCanvasClick(nx,ny) — called when the user single-taps an empty area
 *   onPanZoom({ scale }) — optional listener for the parent UI
 *   addMode            — if true, click anywhere drops a pin; otherwise pan
 *   pins               — array of pin objects (rendered above the image)
 */
export default function SheetCanvas({
  sheetId,
  // FP-059 (2026-09-09 audit remediation) — the sheet's real revision
  // identity. Threaded down to TileLayer so its generation/eviction
  // logic (built in tileIdentity.js) actually checks against something
  // real instead of the constant fallback "r0" every sheet was silently
  // getting because this was never passed at all.
  sheetRevision,
  rasterUrl,
  pins = [],
  renderPin,
  onCanvasClick,
  // Iter 178.7 — accept a broader "placement mode active" prop so asset
  // placement (and any future placement tools) reuse the same tap-to-
  // place path as pin add. Previously addMode was the ONLY trigger,
  // which silently broke asset placement in production.
  placementActive = false,
  onLongPress,
  onPanZoom,
  onSwipe,
  onPinDragEnd,
  // R11 continuation (2026-09-10) — the pin marker's own native `onClick`
  // never actually fires: `wrapRef.current.setPointerCapture(e.pointerId)`
  // is called unconditionally on every pointerdown (see onPointerDown
  // below), and Chromium redirects the synthesized "click" event to
  // whichever element holds pointer capture rather than the original
  // target once capture is set — so the marker's click handler was dead
  // code. This is fired instead, from the SAME pointerup branch that
  // already distinguishes "moved" (drag) from "not moved" (tap).
  onPinTap,
  onMeasureClick,
  onMeasureHover,
  onMeasureScreenMove,    // Iter 144 Phase 2 — fires (clientX, clientY) in screen coords for the precision magnifier overlay
  onMeasurePrecisionHold, // TRACK 1 — fires (active) when the operator holds still for ≥ 250ms on a measure-armed canvas
  measureMode = false,
  pinDragEnabled = false,
  cropMode = false,
  onCropDrag,
  onCropEnd,
  markupMode = false,
  markupTool = null,
  markupInProgress = false,
  onMarkupClick,
  onMarkupHover,
  onMarkupDragStart,
  onMarkupDragMove,
  onMarkupDragEnd,
  onImgDims,
  addMode = false,
  overlay = null,
  focusedStructureId = null,
  onStructurePick = null,
  className = "",
  testid,
}) {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [imgDims, setImgDims] = useState({ w: 1, h: 1 });
  const [containerDims, setContainerDims] = useState({ w: 1, h: 1 });
  // Wave B · Turn 1 real-plan cert fix — surface the raster's true pixel
  // dims to the parent so measure/markup UI outside this canvas (e.g. the
  // Measure toolbar's live angle readout) can share the same
  // aspect-ratio-correct geometry as the on-canvas preview.
  useEffect(() => {
    onImgDims?.(imgDims);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgDims.w, imgDims.h]);
  // ============================================================
  // IP-002 — Canonical Camera Command Authority.
  //
  // Every viewport mutation goes through `cameraReducer` via `dispatch`.
  // The legacy `setVp` name is preserved as a thin adapter so call
  // sites remain readable; each adapter call declares WHICH named
  // command it represents. Dev-mode ledger (see lib/cameraController)
  // asserts unauthorized_camera_mutations = 0 across every interaction.
  // ============================================================
  const [camState, camDispatch] = useReducer(
    cameraReducer, undefined, initialCameraState,
  );
  const vp = camState.vp;
  const vpRef = useRef(vp);
  useEffect(() => { vpRef.current = vp; }, [vp]);
  // Adapter for the historical `setVp(nextOrUpdater)` call pattern.
  // Every call site below is annotated with the command type it maps
  // to (INITIAL_FIT / RESTORE_VIEWPORT / USER_PAN / USER_PINCH_ZOOM /
  // USER_BUTTON_ZOOM / FOCUS_OBJECT / APPLY_ANIMATION_FRAME).
  const setVp = useCallback((nextOrUpdater, cmdMeta) => {
    const type = (cmdMeta && cmdMeta.type) || "APPLY_ANIMATION_FRAME";
    const source = (cmdMeta && cmdMeta.source) || "legacy";
    const user_initiated = !!(cmdMeta && cmdMeta.user_initiated);
    const nextVp = typeof nextOrUpdater === "function"
      ? nextOrUpdater(vpRef.current)
      : nextOrUpdater;
    camDispatch({
      type, source, user_initiated, next: nextVp,
    });
  }, []);

  // Cross-fade — keep the previous raster URL mounted briefly so the sheet
  // transition feels like turning a page on a light table rather than a hard
  // cut. Once the new raster has loaded we animate the old one out.
  //
  // Batch F-tile (Iter 23): for the dpr=1 → dpr=2 retina-sharpen swap, we
  // pre-decode the 2× JPEG off the main thread via HTMLImageElement.decode()
  // BEFORE committing the new URL into the layer state. The visible swap then
  // happens with the bitmap already in GPU-ready form, eliminating the 10MP
  // single-decode stutter that iPads exhibit on large airport sheets. The
  // user perceives a "soft sharpen emergence" rather than a hitched repaint.
  const [layers, setLayers] = useState({ curUrl: rasterUrl, prevUrl: null, newReady: true });
  const pendingDecode = useRef(null);
  useEffect(() => {
    if (rasterUrl === layers.curUrl) return;
    if (!rasterUrl) {
      setLayers({ curUrl: null, prevUrl: layers.curUrl, newReady: false });
      return;
    }
    // Track this swap so an in-flight decode from a stale URL never wins.
    pendingDecode.current = rasterUrl;
    const preload = new Image();
    preload.decoding = "async";
    preload.src = rasterUrl;
    const commit = () => {
      if (pendingDecode.current !== rasterUrl) return;  // a newer URL took over
      setLayers((prev) => ({ curUrl: rasterUrl, prevUrl: prev.curUrl, newReady: false }));
    };
    if (typeof preload.decode === "function") {
      preload.decode().then(commit).catch(commit);  // even on decode error, swap so the browser can show its own placeholder
    } else {
      // Older Safari / Firefox fallback — wait for onLoad instead.
      preload.onload = commit;
      preload.onerror = commit;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rasterUrl]);
  const onNewImgLoad = () => {
    setLayers((prev) => ({ ...prev, newReady: true }));
    // Remove the previous raster after the fade completes.
    setTimeout(() => setLayers((prev) => ({ ...prev, prevUrl: null })), 200);
    onImgLoad();
  };

  // Pin drag state — set when the user pointer-downs on a [data-pin-id]
  // element. Threshold-based: only emits drag updates once movement exceeds
  // ~6px so single-taps still register as taps (the popover still opens).
  const pinDragState = useRef(null); // { pinId, startCX, startCY, moved }
  const [activeDragPin, setActiveDragPin] = useState(null); // { id, nx, ny }

  // ---- Image natural dimensions ----
  const onImgLoad = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;
    setImgDims({ w: img.naturalWidth || 1, h: img.naturalHeight || 1 });
  }, []);

  // ---- Container size tracking ----
  // Iter 168 — Field-Trust Recovery.
  // The ResizeObserver ONLY records new container dimensions. A
  // separate idempotent effect (below) compensates the viewport
  // translation when containerDims actually changes so the sheet-space
  // point at the visual centre of the container stays put. This design
  // is StrictMode-safe (the effect is idempotent — running it twice
  // still lands on the same tx/ty because it always uses the current
  // `prev` snapshot from prevContainerRef which is only advanced once).
  const prevContainerRef = useRef(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const applyDims = () => {
      setContainerDims({ w: el.clientWidth, h: el.clientHeight });
    };
    const ro = new ResizeObserver(applyDims);
    ro.observe(el);
    applyDims();
    return () => ro.disconnect();
  }, []);

  // Idempotent container-resize center preservation. Advances
  // prevContainerRef exactly once per real dims change so a StrictMode
  // double-invocation is a no-op the second time.
  useEffect(() => {
    if (!containerDims.w || !containerDims.h) return;
    const prev = prevContainerRef.current;
    if (!prev) {
      prevContainerRef.current = { w: containerDims.w, h: containerDims.h };
      return;
    }
    const dx = (containerDims.w - prev.w) / 2;
    const dy = (containerDims.h - prev.h) / 2;
    if (dx === 0 && dy === 0) return;
    prevContainerRef.current = { w: containerDims.w, h: containerDims.h };
    if (initialized.current) {
      // IP-002 · PRESERVE_CENTER_AFTER_RESIZE
      setVp((v) => ({ ...v, tx: v.tx + dx, ty: v.ty + dy }), {
        type: "PRESERVE_CENTER_AFTER_RESIZE",
        source: "container_resize",
        user_initiated: false,
      });
    }
  }, [containerDims, setVp]);

  // ---- Compute "fit-to-window" scale (delegated to lib/camera.js) ----
  const fitScale = useMemo(() => computeFitScale(imgDims, containerDims), [imgDims, containerDims]);

  // ---- Initialize viewport from storage or fit ----
  const initialized = useRef(false);
  useEffect(() => {
    // Wait for a real image to load (natural width > 100 = actual image, not 1×1 default).
    if (imgDims.w < 100 || !containerDims.w || initialized.current) return;
    initialized.current = true;
    const fitS = fitScale || 1;
    const stored = readViewport(sheetId);
    if (stored && isPlausibleViewport(stored, fitS)) {
      // IP-002 · RESTORE_VIEWPORT
      setVp(stored, {
        type: "RESTORE_VIEWPORT",
        source: "persistence_hydrate",
        user_initiated: false,
      });
      return;
    }
    // IP-002 · INITIAL_FIT
    setVp(fitAndCenter(imgDims, containerDims), {
      type: "INITIAL_FIT",
      source: "sheet_open",
      user_initiated: false,
    });
  }, [imgDims, containerDims, fitScale, sheetId, setVp]);

  // Iter 264 root-cause fix — "Fit to screen" showing 15% instead of ~59%.
  // imgDims tracks the CURRENTLY LOADED raster's native pixel size, which
  // the TileManager deliberately changes as it escalates/demotes DPR
  // (dpr=1 -> 2400x1600, dpr=4 -> 9600x6400, same logical sheet). imgDims
  // feeds the world layer's CSS width/height directly, so a DPR swap that
  // happens WITHOUT rescaling vp.s makes the apparent on-screen size jump
  // by the DPR ratio. In the reported repro, a deep wheel-zoom escalated
  // to dpr=4, "Fit to screen" correctly computed vp.s for THAT imgDims,
  // then the tile manager demoted back to dpr=1 (ratio now <1.3x) —
  // imgDims shrank 4x under an unchanged vp.s, collapsing the visible
  // sheet to ~1/4 size. Keep vp.s inversely proportional to imgDims so a
  // DPR swap never changes the apparent framing.
  const prevImgDimsForScale = useRef(null);
  useEffect(() => {
    if (imgDims.w < 100 || !initialized.current) return;
    const prev = prevImgDimsForScale.current;
    if (!prev) {
      prevImgDimsForScale.current = imgDims;
      return;
    }
    if (prev.w === imgDims.w && prev.h === imgDims.h) return;
    const ratio = prev.w / imgDims.w; // e.g. dpr 4 -> 1 demotion: 9600/2400 = 4
    prevImgDimsForScale.current = imgDims;
    if (!Number.isFinite(ratio) || ratio <= 0 || ratio === 1) return;
    setVp((cur) => ({ ...cur, s: cur.s * ratio }), {
      type: "APPLY_ANIMATION_FRAME",
      source: "dpr_tile_swap_compensation",
      user_initiated: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgDims.w, imgDims.h]);

  // Reset persistence + viewport when the sheet changes.
  useEffect(() => {
    initialized.current = false;
  }, [sheetId]);

  // Persist viewport (debounced via rAF). Skip until canvas is initialized.
  const persistRaf = useRef(0);
  useEffect(() => {
    if (!initialized.current) return;
    cancelAnimationFrame(persistRaf.current);
    persistRaf.current = requestAnimationFrame(() => { writeViewport(sheetId, vp); });
    if (onPanZoom) onPanZoom({ scale: vp.s, fitScale, tx: vp.tx, ty: vp.ty });
  }, [vp, sheetId, onPanZoom, fitScale]);

  // ---- Helpers ----
  const clampScale = useCallback((s) => clampScaleLib(s, fitScale), [fitScale]);

  // ============================================================
  // PILLAR 2A — Touch Precision: Offset Target Model.
  //
  // Desktop has hover; iPad does not. The finger covers the exact spot
  // where the point should land. We fix this by offsetting the target
  // 56px ABOVE the finger contact whenever a tap-tool (measure or
  // markup-tap) is armed AND the event came from a touch surface.
  //
  // Result: the operator sees a sticky crosshair above their finger
  // that tracks while they drag, and the commit lands at the crosshair —
  // not at the finger position. Bluebeam-iPad equivalent.
  //
  // Apple-Pencil and Mouse traffic is untouched: hover behaves as before.
  // ============================================================
  const TOUCH_TARGET_OFFSET_Y = 56;
  const isTouchOffsetActive = (e) => (
    e && e.pointerType === "touch" && (
      measureMode ||
      (markupMode && markupTool && markupTool !== "freehand")
    )
  );
  // Returns the adjusted clientY to use when computing image coords for
  // a measure/markup tap. clientX is never offset because finger wobble
  // on X is symmetric; only Y benefits from the lift-above-finger trick.
  const adjustedClientY = (e) => (
    isTouchOffsetActive(e) ? e.clientY - TOUCH_TARGET_OFFSET_Y : e.clientY
  );

  // Rubber-band constant (kept here as well for in-component physics use).
  const RUBBER_C = 0.55;
  const rubberBand = (delta, dim) => {
    const d = Math.max(1, dim);
    return (delta * RUBBER_C * d) / (d + RUBBER_C * delta);
  };
  // Apply elastic constraint to a translation value. When tx exceeds the
  // natural bounds the excess gets rubber-banded.
  const constrainTx = (tx, s = vpRef.current.s) => {
    if (!imgDims.w || !containerDims.w) return tx;
    const imgW = imgDims.w * s;
    if (imgW <= containerDims.w) {
      const center = (containerDims.w - imgW) / 2;
      const delta = tx - center;
      const elastic = Math.sign(delta) * rubberBand(Math.abs(delta), containerDims.w);
      return center + elastic;
    }
    const minTx = containerDims.w - imgW;
    const maxTx = 0;
    if (tx > maxTx) return maxTx + rubberBand(tx - maxTx, containerDims.w);
    if (tx < minTx) return minTx - rubberBand(minTx - tx, containerDims.w);
    return tx;
  };
  const constrainTy = (ty, s = vpRef.current.s) => {
    if (!imgDims.h || !containerDims.h) return ty;
    const imgH = imgDims.h * s;
    if (imgH <= containerDims.h) {
      const center = (containerDims.h - imgH) / 2;
      const delta = ty - center;
      const elastic = Math.sign(delta) * rubberBand(Math.abs(delta), containerDims.h);
      return center + elastic;
    }
    const minTy = containerDims.h - imgH;
    const maxTy = 0;
    if (ty > maxTy) return maxTy + rubberBand(ty - maxTy, containerDims.h);
    if (ty < minTy) return minTy - rubberBand(minTy - ty, containerDims.h);
    return ty;
  };
  // Snap target — the nearest "natural bound" tx/ty (no rubber). Used on
  // pointer-up to ease back from overscroll.
  const snapTarget = (s = vpRef.current.s) => {
    let tx = vpRef.current.tx, ty = vpRef.current.ty;
    if (imgDims.w && containerDims.w) {
      const imgW = imgDims.w * s;
      if (imgW <= containerDims.w) tx = (containerDims.w - imgW) / 2;
      else tx = Math.min(0, Math.max(containerDims.w - imgW, tx));
    }
    if (imgDims.h && containerDims.h) {
      const imgH = imgDims.h * s;
      if (imgH <= containerDims.h) ty = (containerDims.h - imgH) / 2;
      else ty = Math.min(0, Math.max(containerDims.h - imgH, ty));
    }
    return { tx, ty, s };
  };
  // Tests `vp` (or current ref) for overscroll. Pass the live vp state when
  // checking from JSX (vpRef lags by one render cycle); pass nothing inside
  // pointer/inertia handlers (vpRef is already current at that point).
  const isOverscrolledOf = (vpLive) => {
    const tx = vpLive ? vpLive.tx : vpRef.current.tx;
    const ty = vpLive ? vpLive.ty : vpRef.current.ty;
    const s  = vpLive ? vpLive.s  : vpRef.current.s;
    let tTx = tx, tTy = ty;
    if (imgDims.w && containerDims.w) {
      const imgW = imgDims.w * s;
      tTx = imgW <= containerDims.w
        ? (containerDims.w - imgW) / 2
        : Math.min(0, Math.max(containerDims.w - imgW, tx));
    }
    if (imgDims.h && containerDims.h) {
      const imgH = imgDims.h * s;
      tTy = imgH <= containerDims.h
        ? (containerDims.h - imgH) / 2
        : Math.min(0, Math.max(containerDims.h - imgH, ty));
    }
    return Math.abs(tTx - tx) > 0.5 || Math.abs(tTy - ty) > 0.5;
  };
  const isOverscrolled = () => isOverscrolledOf(null);

  // Zoom around a screen point (px, py) — the natural map-like feel.
  // IP-002: this helper is called by pinch, wheel, and button-zoom.
  // The `source` arg lets the caller declare which authorized command
  // is being dispatched, so ledger provenance stays accurate.
  const zoomAroundPoint = useCallback((nextScale, px, py, source = "wheel") => {
    const cmdType = source === "pinch"
      ? "USER_PINCH_ZOOM"
      : source === "trackpad"
        ? "USER_TRACKPAD_ZOOM"
        : source === "button"
          ? "USER_BUTTON_ZOOM"
          : "USER_WHEEL_ZOOM";
    setVp((prev) => {
      const s2 = clampScale(nextScale);
      const ratio = s2 / prev.s;
      const tx = px - (px - prev.tx) * ratio;
      const ty = py - (py - prev.ty) * ratio;
      return { tx, ty, s: s2 };
    }, { type: cmdType, source, user_initiated: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitScale]);

  // ---- Pointer interaction (pan + pinch) ----
  const ptrs = useRef(new Map()); // pointerId → {x,y}
  const dragState = useRef(null);  // { startTx, startTy, startX, startY, moved }
  const pinchState = useRef(null); // { startDist, startScale, midX, midY }
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);

  // TRACK 1 — Precision Loupe via long-press hold.
  // When the operator presses on a measure-armed canvas and keeps their
  // finger within an 8 px radius for ≥ 250 ms, we flip a "precision hold"
  // flag. The viewer turns on the magnifier ONLY while this flag is true,
  // so the loupe cannot flicker during rapid drags. Released the moment
  // the finger lifts or moves more than 18 px.
  const precisionHoldTimer = useRef(null);
  const precisionHoldOriginRef = useRef(null);
  const setPrecisionHold = (active) => {
    if (onMeasurePrecisionHold) {
      try { onMeasurePrecisionHold(!!active); } catch { /* swallow */ }
    }
  };
  const clearPrecisionHold = () => {
    if (precisionHoldTimer.current) {
      clearTimeout(precisionHoldTimer.current);
      precisionHoldTimer.current = null;
    }
    precisionHoldOriginRef.current = null;
    setPrecisionHold(false);
  };

  // Velocity tracking for pan inertia — last 5 samples.
  const velSamples = useRef([]); // [{x, y, t}]
  const inertiaRaf = useRef(0);
  const animRaf = useRef(0);

  // Double-tap detection.
  const lastTap = useRef({ t: 0, x: 0, y: 0 });

  const cancelInertia = () => {
    if (inertiaRaf.current) {
      cancelAnimationFrame(inertiaRaf.current);
      inertiaRaf.current = 0;
    }
  };
  const cancelAnim = () => {
    if (animRaf.current) {
      cancelAnimationFrame(animRaf.current);
      animRaf.current = 0;
    }
  };

  // Animate viewport to a target over `dur` ms with easeOutCubic.
  // The "snap to fit" / sheet-jump smooth zoom uses this.
  const animateTo = useCallback((target, dur = 280) => {
    cancelAnim();
    cancelInertia();
    const start = { ...vpRef.current };
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const step = (now) => {
      const k = Math.min(1, (now - t0) / dur);
      const e = ease(k);
      // IP-002 · APPLY_ANIMATION_FRAME (system snap-back animation)
      setVp({
        tx: start.tx + (target.tx - start.tx) * e,
        ty: start.ty + (target.ty - start.ty) * e,
        s: start.s + (target.s - start.s) * e,
      }, {
        type: "APPLY_ANIMATION_FRAME",
        source: "snap_back_animation",
        user_initiated: false,
      });
      if (k < 1) animRaf.current = requestAnimationFrame(step);
      else animRaf.current = 0;
    };
    animRaf.current = requestAnimationFrame(step);
  }, [setVp]);

  const cancelLongPress = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const screenToImage = (px, py) => {
    // wrapRef bounding rect — compute local coords
    const rect = wrapRef.current?.getBoundingClientRect();
    const lx = px - (rect?.left || 0);
    const ly = py - (rect?.top || 0);
    return { lx, ly };
  };

  const onPointerDown = (e) => {
    const el = wrapRef.current;
    if (!el) return;
    try { el.setPointerCapture(e.pointerId); } catch {}
    cancelInertia();
    cancelAnim();
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // Detect pin drag — if the pointer-down target is inside a pin marker,
    // start the drag state machine instead of pan.
    if (pinDragEnabled && ptrs.current.size === 1) {
      const target = e.target;
      const pinEl = target?.closest && target.closest('[data-pin-id]');
      if (pinEl) {
        const pinId = pinEl.getAttribute("data-pin-id");
        pinDragState.current = {
          pinId,
          startCX: e.clientX,
          startCY: e.clientY,
          moved: false,
        };
        // Do NOT start the normal drag — pan should not move while we're
        // dragging a pin. We also skip long-press, since this is a press-
        // to-relocate gesture, not a press-to-mark.
        dragState.current = null;
        return;
      }
    }

    if (ptrs.current.size === 1) {
      const { lx, ly } = screenToImage(e.clientX, e.clientY);
      velSamples.current = [{ x: lx, y: ly, t: performance.now() }];
      // Crop mode: dragState becomes a crop-rect builder instead of pan.
      if (cropMode) {
        const ix = (lx - vpRef.current.tx) / vpRef.current.s;
        const iy = (ly - vpRef.current.ty) / vpRef.current.s;
        const nx = Math.max(0, Math.min(1, ix / imgDims.w));
        const ny = Math.max(0, Math.min(1, iy / imgDims.h));
        dragState.current = {
          mode: "crop",
          nx1: nx, ny1: ny,
          startX: lx, startY: ly,
          moved: false,
        };
        if (onCropDrag) onCropDrag({ nx1: nx, ny1: nx, nx2: nx, ny2: ny });
        return;
      }
      // Markup-freehand mode: drag captures path.
      if (markupMode && markupTool === "freehand") {
        const ix = (lx - vpRef.current.tx) / vpRef.current.s;
        const iy = (ly - vpRef.current.ty) / vpRef.current.s;
        const nx = Math.max(0, Math.min(1, ix / imgDims.w));
        const ny = Math.max(0, Math.min(1, iy / imgDims.h));
        dragState.current = { mode: "freehand", moved: false };
        if (onMarkupDragStart) onMarkupDragStart(nx, ny);
        return;
      }
      // Markup 2-tap shape tools (arrow/rectangle/circle/cloud/highlight/
      // callout): support press->drag->release as ONE gesture (Bluebeam
      // habit), not just tap-then-tap. Point 1 seeds at drag-start once
      // the gesture crosses the move threshold (see onPointerMove);
      // point 2 commits on release (see onPointerUp below). A plain tap
      // (no movement) still falls through to the ordinary tap-then-tap
      // flow via the single onMarkupClick call made on release.
      if (markupMode && markupTool && markupTool !== "freehand" && markupTool !== "text") {
        const ix0 = (lx - vpRef.current.tx) / vpRef.current.s;
        const iy0 = (ly - vpRef.current.ty) / vpRef.current.s;
        const nx0 = Math.max(0, Math.min(1, ix0 / imgDims.w));
        const ny0 = Math.max(0, Math.min(1, iy0 / imgDims.h));
        if (onMarkupHover && imgDims.w > 0 && imgDims.h > 0 && ix0 >= 0 && iy0 >= 0 && ix0 <= imgDims.w && iy0 <= imgDims.h) {
          onMarkupHover(nx0, ny0);
        }
        dragState.current = {
          mode: "markup_shape",
          startX: lx, startY: ly,
          startNx: nx0, startNy: ny0,
          moved: false,
        };
        return;
      }
      // Iter FIELD-UX-1 — LIVE TOUCH PREVIEW + PILLAR 2A TOUCH OFFSET.
      // On iPad / touch devices the operator complaint is "blind taps":
      // after tap A, lifting your finger removes any preview, so tap B
      // is committed without seeing where the line will land. We fire
      // the hover callbacks IMMEDIATELY on pointerdown so the rubber-band
      // appears the moment the finger contacts. On touch the target is
      // additionally lifted 56px above the finger so it isn't hidden by
      // the hand. See TOUCH_TARGET_OFFSET_Y at the top of this file.
      const _touchY = adjustedClientY(e);
      const _isTouchTap = isTouchOffsetActive(e);
      if (measureMode && onMeasureHover && imgDims.w > 0 && imgDims.h > 0) {
        const { lx: alx, ly: aly } = screenToImage(e.clientX, _touchY);
        const ix = (alx - vpRef.current.tx) / vpRef.current.s;
        const iy = (aly - vpRef.current.ty) / vpRef.current.s;
        if (ix >= 0 && iy >= 0 && ix <= imgDims.w && iy <= imgDims.h) {
          onMeasureHover(ix / imgDims.w, iy / imgDims.h);
          if (onMeasureScreenMove) onMeasureScreenMove(e.clientX, _touchY, _isTouchTap);
        }
        // TRACK 1 — start precision-hold timer. The MeasureMagnifier
        // turns on only after a 250ms still-finger hold; this prevents
        // the historical flicker during quick drags. Cancelled if the
        // finger moves > 18px (precisionHoldOriginRef check below) or
        // if pointer-up fires before the timer.
        precisionHoldOriginRef.current = { x: e.clientX, y: e.clientY };
        if (precisionHoldTimer.current) clearTimeout(precisionHoldTimer.current);
        precisionHoldTimer.current = setTimeout(() => {
          // re-check the finger hasn't moved during the wait window
          const origin = precisionHoldOriginRef.current;
          if (!origin) return;
          setPrecisionHold(true);
        }, 250);
      }
      if (markupMode && markupTool && markupTool !== "freehand" && onMarkupHover && imgDims.w > 0 && imgDims.h > 0) {
        const { lx: alx, ly: aly } = screenToImage(e.clientX, _touchY);
        const ix = (alx - vpRef.current.tx) / vpRef.current.s;
        const iy = (aly - vpRef.current.ty) / vpRef.current.s;
        if (ix >= 0 && iy >= 0 && ix <= imgDims.w && iy <= imgDims.h) {
          onMarkupHover(ix / imgDims.w, iy / imgDims.h);
        }
      }
      dragState.current = {
        startTx: vp.tx, startTy: vp.ty,
        startX: lx, startY: ly,
        moved: false,
      };
      // Long-press detection — fires onLongPress after 600ms if no drag.
      longPressFired.current = false;
      cancelLongPress();
      if (onLongPress && !addMode && !placementActive) {
        const startCX = e.clientX, startCY = e.clientY;
        longPressTimer.current = setTimeout(() => {
          if (dragState.current && !dragState.current.moved) {
            const { lx: cx, ly: cy } = screenToImage(startCX, startCY);
            const ix = (cx - vp.tx) / vp.s;
            const iy = (cy - vp.ty) / vp.s;
            if (ix >= 0 && iy >= 0 && ix <= imgDims.w && iy <= imgDims.h) {
              longPressFired.current = true;
              onLongPress(ix / imgDims.w, iy / imgDims.h);
            }
          }
        }, 600);
      }
    } else if (ptrs.current.size === 2) {
      cancelLongPress();
      const pts = Array.from(ptrs.current.values());
      const dx = pts[1].x - pts[0].x;
      const dy = pts[1].y - pts[0].y;
      const dist = Math.hypot(dx, dy);
      const midX = (pts[0].x + pts[1].x) / 2;
      const midY = (pts[0].y + pts[1].y) / 2;
      pinchState.current = { startDist: dist, startScale: vp.s, midX, midY };
      dragState.current = null;
    }
  };

  const onPointerMove = (e) => {
    // Iter FIELD-UX-1 — Desktop mouse-hover preview.
    // pointermove fires for mouse-without-button too. The early return
    // below blocks those events because no pointerdown was registered.
    // Before returning, give measure/markup hover a chance to fire so
    // the desktop operator sees the rubber-band as their cursor moves
    // between clicks. Touch devices don't fire hover-without-touch so
    // this is purely a desktop quality-of-life fix that doesn't change
    // touch behavior.
    if (!ptrs.current.has(e.pointerId)) {
      if (e.pointerType === "mouse" && imgDims.w > 0 && imgDims.h > 0) {
        const { lx, ly } = screenToImage(e.clientX, e.clientY);
        const ix = (lx - vpRef.current.tx) / vpRef.current.s;
        const iy = (ly - vpRef.current.ty) / vpRef.current.s;
        if (ix >= 0 && iy >= 0 && ix <= imgDims.w && iy <= imgDims.h) {
          if (measureMode && onMeasureHover) {
            onMeasureHover(ix / imgDims.w, iy / imgDims.h);
            if (onMeasureScreenMove) onMeasureScreenMove(e.clientX, e.clientY);
          }
          if (markupMode && markupTool && markupTool !== "freehand" && onMarkupHover) {
            onMarkupHover(ix / imgDims.w, iy / imgDims.h);
          }
        }
      }
      return;
    }
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    // ---------- Pin drag tracking ----------
    // Iter 143.1 — capture pinDragState.current once. iPad Safari can fire
    // pointermove after a pointercancel cleared the ref mid-handler.
    const pds = pinDragState.current;
    if (pds) {
      const { startCX, startCY, pinId } = pds;
      const dCx = e.clientX - startCX;
      const dCy = e.clientY - startCY;
      if (!pds.moved && Math.hypot(dCx, dCy) > 6) {
        pds.moved = true;
      }
      if (pds.moved) {
        const { lx, ly } = screenToImage(e.clientX, e.clientY);
        const ix = (lx - vpRef.current.tx) / vpRef.current.s;
        const iy = (ly - vpRef.current.ty) / vpRef.current.s;
        const safeW = imgDims.w > 0 ? imgDims.w : 1;
        const safeH = imgDims.h > 0 ? imgDims.h : 1;
        const nx = Math.max(0, Math.min(1, ix / safeW));
        const ny = Math.max(0, Math.min(1, iy / safeH));
        if (Number.isFinite(nx) && Number.isFinite(ny)) {
          setActiveDragPin({ id: pinId, nx, ny });
        }
      }
      return;
    }

    // ---------- Measure hover (when single point already placed) ----------
    if (measureMode && onMeasureHover && ptrs.current.size === 1 && !dragState.current?.moved) {
      // PILLAR 2A — apply touch-target offset so the operator sees the
      // crosshair above the finger instead of under it.
      const _isTouchTap = isTouchOffsetActive(e);
      const _adjY = adjustedClientY(e);
      const { lx: alx, ly: aly } = screenToImage(e.clientX, _adjY);
      const ix = (alx - vpRef.current.tx) / vpRef.current.s;
      const iy = (aly - vpRef.current.ty) / vpRef.current.s;
      // Iter 143 — guard against pre-decode dimensions (0/NaN) producing
      // Infinity which crashes downstream consumers.
      if (imgDims.w > 0 && imgDims.h > 0 &&
          ix >= 0 && iy >= 0 && ix <= imgDims.w && iy <= imgDims.h) {
        onMeasureHover(ix / imgDims.w, iy / imgDims.h);
        // Iter 144 Phase 2 — fire raw screen coords for the precision
        // magnifier overlay. PILLAR 2A — pass the OFFSET clientY plus a
        // touch flag so the floating crosshair lands on the target, not
        // on the finger.
        if (onMeasureScreenMove) onMeasureScreenMove(e.clientX, _adjY, _isTouchTap);
      }
    }
    // ---------- Markup hover (live shape preview while finger held) ----------
    // Iter FIELD-UX-1 — operators reported markups feel "blind". Wire
    // onMarkupHover during a held single-finger so the rubber-band
    // shape (arrow/rect/circle/cloud/highlight) follows the finger
    // before the second tap is committed.
    if (markupMode && markupTool && markupTool !== "freehand" && onMarkupHover &&
        ptrs.current.size === 1 && !dragState.current?.moved) {
      const _adjY = adjustedClientY(e);
      const { lx: alx, ly: aly } = screenToImage(e.clientX, _adjY);
      const ix = (alx - vpRef.current.tx) / vpRef.current.s;
      const iy = (aly - vpRef.current.ty) / vpRef.current.s;
      if (imgDims.w > 0 && imgDims.h > 0 &&
          ix >= 0 && iy >= 0 && ix <= imgDims.w && iy <= imgDims.h) {
        onMarkupHover(ix / imgDims.w, iy / imgDims.h);
      }
    }
    if (ptrs.current.size === 2 && pinchState.current) {
      const pts = Array.from(ptrs.current.values());
      // Iter 143 — iPad pinch hardening. Two-finger frames can race in
      // ways that produce dist=0, startDist=0, or undefined scale, all
      // of which cascade into NaN/Infinity through the camera reducer
      // and crash the React tree. Guard explicitly.
      // Iter 143.1 — capture pinchState.current once; the ref can be
      // nulled (line 539) between this check and downstream reads if
      // pointercancel arrives mid-handler on iPad Safari.
      const pinch = pinchState.current;
      if (!pinch || pts.length < 2) return;
      const dx = pts[1].x - pts[0].x;
      const dy = pts[1].y - pts[0].y;
      const dist = Math.hypot(dx, dy);
      const startDist = pinch.startDist;
      const startScale = pinch.startScale;
      if (!Number.isFinite(dist) || !Number.isFinite(startDist) ||
          !Number.isFinite(startScale) || startDist <= 0) {
        return;
      }
      let next = startScale * (dist / startDist);
      // Clamp to the same range the wheel/UI zoom uses so nothing
      // explodes into the camera state machine.
      if (!Number.isFinite(next)) return;
      next = Math.max(0.05, Math.min(40, next));
      const { lx, ly } = screenToImage(pinch.midX, pinch.midY);
      if (!Number.isFinite(lx) || !Number.isFinite(ly)) return;
      zoomAroundPoint(next, lx, ly, "pinch");
      return;
    }
    if (ptrs.current.size === 1 && dragState.current) {
      // Iter 143.1 — capture dragState.current once. The setVp updater
      // below is invoked asynchronously by React's scheduler; in the
      // interval, a pointerup/pointercancel (very common on iPad Safari
      // touch coalescing) can null dragState.current and cause a
      // `null is not an object (evaluating 'oe.current.startTx')` crash.
      const ds = dragState.current;
      // Crop mode — update the rect's second corner.
      if (ds.mode === "crop") {
        const { lx, ly } = screenToImage(e.clientX, e.clientY);
        const ix = (lx - vpRef.current.tx) / vpRef.current.s;
        const iy = (ly - vpRef.current.ty) / vpRef.current.s;
        // Iter 143 — pre-decode imgDims is {w:1,h:1} from useState init;
        // guard division for the brief window before the image lands.
        const safeW = imgDims.w > 0 ? imgDims.w : 1;
        const safeH = imgDims.h > 0 ? imgDims.h : 1;
        const nx2 = Math.max(0, Math.min(1, ix / safeW));
        const ny2 = Math.max(0, Math.min(1, iy / safeH));
        ds.moved = true;
        if (onCropDrag) onCropDrag({ nx1: ds.nx1, ny1: ds.ny1, nx2, ny2 });
        return;
      }
      // Freehand markup — append point.
      if (ds.mode === "freehand") {
        const { lx, ly } = screenToImage(e.clientX, e.clientY);
        const ix = (lx - vpRef.current.tx) / vpRef.current.s;
        const iy = (ly - vpRef.current.ty) / vpRef.current.s;
        const safeW = imgDims.w > 0 ? imgDims.w : 1;
        const safeH = imgDims.h > 0 ? imgDims.h : 1;
        const nx = Math.max(0, Math.min(1, ix / safeW));
        const ny = Math.max(0, Math.min(1, iy / safeH));
        ds.moved = true;
        if (onMarkupDragMove) onMarkupDragMove(nx, ny);
        return;
      }
      // Markup shape tools — press->drag->release as one gesture. Once
      // the drag crosses the tap/pan threshold, seed point 1 at the
      // DRAG-START position (unless a point was already placed by an
      // earlier separate tap) and keep the live preview following the
      // finger for the rest of the drag.
      if (ds.mode === "markup_shape") {
        const { lx, ly } = screenToImage(e.clientX, e.clientY);
        const dx = lx - ds.startX;
        const dy = ly - ds.startY;
        if (!ds.moved && (Math.abs(dx) + Math.abs(dy)) > 12) {
          ds.moved = true;
          cancelLongPress();
          if (!markupInProgress && onMarkupClick) {
            onMarkupClick(ds.startNx, ds.startNy);
          }
        }
        if (ds.moved && onMarkupHover && imgDims.w > 0 && imgDims.h > 0) {
          const ix = (lx - vpRef.current.tx) / vpRef.current.s;
          const iy = (ly - vpRef.current.ty) / vpRef.current.s;
          const nx = Math.max(0, Math.min(1, ix / imgDims.w));
          const ny = Math.max(0, Math.min(1, iy / imgDims.h));
          onMarkupHover(nx, ny);
        }
        return;
      }
      const { lx, ly } = screenToImage(e.clientX, e.clientY);
      // Iter 143.1 — guard ds.startX/Y in case the start position was
      // never captured (defensive; should always exist by this branch).
      const startX = Number.isFinite(ds.startX) ? ds.startX : lx;
      const startY = Number.isFinite(ds.startY) ? ds.startY : ly;
      const dx = lx - startX;
      const dy = ly - startY;
      // LAST-CHANCE RESCUE — Sticky-tap during measure / markup-tap tools.
      // A glove on iPad can wobble a finger 5–10px between contact and
      // release. With a 3px threshold the operator's tap was committing
      // a pan and DROPPING the measure click → "the drawing jumped, my
      // tap didn't land." Bluebeam uses ~12px. We do the same — but only
      // for tap-based tools; for pure pan/zoom the 3px stays.
      const isTapTool =
        measureMode ||
        (markupMode && markupTool && markupTool !== "freehand");
      const moveThreshold = isTapTool ? 12 : 3;
      if (Math.abs(dx) + Math.abs(dy) > moveThreshold) {
        ds.moved = true;
        cancelLongPress();
      }
      // TRACK 1 — release precision-loupe hold if the finger has drifted
      // outside the 18px stability ring. This guarantees the loupe never
      // chases a moving finger (the historical flicker source).
      if (precisionHoldOriginRef.current) {
        const odx = e.clientX - precisionHoldOriginRef.current.x;
        const ody = e.clientY - precisionHoldOriginRef.current.y;
        if (Math.hypot(odx, ody) > 18) {
          clearPrecisionHold();
        }
      }
      // Track velocity (last ~5 samples, ~80ms window).
      const now = performance.now();
      velSamples.current.push({ x: lx, y: ly, t: now });
      if (velSamples.current.length > 6) velSamples.current.shift();
      // Iter 143.1 — Capture startTx/startTy into locals BEFORE the async
      // setVp updater closure. Reading them off dragState.current inside
      // the updater is the documented crash: by the time React runs the
      // updater, pointerup may have set dragState.current = null.
      const startTx = Number.isFinite(ds.startTx) ? ds.startTx : 0;
      const startTy = Number.isFinite(ds.startTy) ? ds.startTy : 0;
      // IP-002 · USER_PAN (pointer drag)
      setVp((prev) => ({
        ...prev,
        tx: constrainTx(startTx + dx, prev.s),
        ty: constrainTy(startTy + dy, prev.s),
      }), { type: "USER_PAN", source: "pointer_drag", user_initiated: true });
    }
  };

  const onPointerUp = (e) => {
    const wasMoved = dragState.current?.moved;
    const fired = longPressFired.current;
    cancelLongPress();
    // TRACK 1 — release precision-loupe on lift.
    clearPrecisionHold();
    ptrs.current.delete(e.pointerId);
    if (ptrs.current.size < 2) pinchState.current = null;

    // ---------- Pin drag release ----------
    if (pinDragState.current) {
      const ps = pinDragState.current;
      pinDragState.current = null;
      if (ps.moved && activeDragPin && onPinDragEnd) {
        onPinDragEnd(ps.pinId, activeDragPin.nx, activeDragPin.ny);
      } else if (!ps.moved && onPinTap) {
        // R11 continuation (2026-09-10) — this used to say "let the pin
        // handle its own click", but pointer capture (see onPointerDown)
        // means the marker's click handler never actually fires. This is
        // now the one real source of "tap opened the pin popover".
        onPinTap(ps.pinId);
      }
      setActiveDragPin(null);
      return;
    }

    if (ptrs.current.size === 0) {
      // Crop mode: finalize the rectangle.
      if (dragState.current?.mode === "crop") {
        const ds = dragState.current;
        dragState.current = null;
        if (onCropEnd) onCropEnd({ moved: ds.moved });
        return;
      }
      // Freehand mode: end.
      if (dragState.current?.mode === "freehand") {
        dragState.current = null;
        if (onMarkupDragEnd) onMarkupDragEnd();
        return;
      }
      // Markup shape tools (arrow/rectangle/circle/cloud/highlight/
      // callout): release commits. For a plain tap this is the ONLY
      // onMarkupClick call (identical to the old tap-then-tap flow, one
      // gesture = one tap). For a press->drag->release, point 1 was
      // already seeded at drag-start in onPointerMove — this call
      // commits point 2, completing the shape in a single gesture.
      if (dragState.current?.mode === "markup_shape") {
        dragState.current = null;
        longPressFired.current = false;
        if (onMarkupClick) {
          const _adjY = adjustedClientY(e);
          const { lx: alx, ly: aly } = screenToImage(e.clientX, _adjY);
          const ix = (alx - vpRef.current.tx) / vpRef.current.s;
          const iy = (aly - vpRef.current.ty) / vpRef.current.s;
          if (ix >= 0 && iy >= 0 && ix <= imgDims.w && iy <= imgDims.h) {
            try { if (navigator.vibrate) navigator.vibrate(20); } catch { /* no-op */ }
            onMarkupClick(ix / imgDims.w, iy / imgDims.h);
          }
        }
        return;
      }
      // Markup click-based tools (taps≥1, non-freehand).
      if (markupMode && markupTool && markupTool !== "freehand" && !wasMoved && !fired && onMarkupClick) {
        // PILLAR 2A — commit at the offset target on touch.
        const _adjY = adjustedClientY(e);
        const { lx: alx, ly: aly } = screenToImage(e.clientX, _adjY);
        const ix = (alx - vpRef.current.tx) / vpRef.current.s;
        const iy = (aly - vpRef.current.ty) / vpRef.current.s;
        if (ix >= 0 && iy >= 0 && ix <= imgDims.w && iy <= imgDims.h) {
          // Iter EXP-1 — haptic confirmation.
          try { if (navigator.vibrate) navigator.vibrate(20); } catch { /* no-op */ }
          onMarkupClick(ix / imgDims.w, iy / imgDims.h);
        }
        dragState.current = null;
        longPressFired.current = false;
        return;
      }
      // ---------- Measure mode click handling ----------
      if (measureMode && !wasMoved && !fired && onMeasureClick) {
        // PILLAR 2A — commit at the offset target so what the operator
        // sees (the floating crosshair above the finger) is what lands.
        const _adjY = adjustedClientY(e);
        const { lx: alx, ly: aly } = screenToImage(e.clientX, _adjY);
        const ix = (alx - vpRef.current.tx) / vpRef.current.s;
        const iy = (aly - vpRef.current.ty) / vpRef.current.s;
        if (ix >= 0 && iy >= 0 && ix <= imgDims.w && iy <= imgDims.h) {
          // Iter EXP-1 — haptic confirmation. Field crews wearing gloves
          // can't always SEE the tap registered; they CAN feel it.
          // navigator.vibrate is supported in iOS Safari PWA and Android.
          try { if (navigator.vibrate) navigator.vibrate(20); } catch { /* no-op */ }
          onMeasureClick(ix / imgDims.w, iy / imgDims.h);
        }
        dragState.current = null;
        longPressFired.current = false;
        return;
      }
      // ---------- Double-tap-to-fit (no drag, two taps within 320ms) ----------
      if (!wasMoved && !fired && !addMode && !placementActive && !measureMode) {
        const now = performance.now();
        const { x: lx, y: ly } = (() => {
          const r = screenToImage(e.clientX, e.clientY);
          return { x: r.lx, y: r.ly };
        })();
        const dist = Math.hypot(lx - lastTap.current.x, ly - lastTap.current.y);
        if (now - lastTap.current.t < 320 && dist < 28) {
          // Double-tap fires: animate to fit (or zoom-in if already at fit).
          const atFit = Math.abs(vpRef.current.s - fitScale) / fitScale < 0.03;
          const ix = (lx - vpRef.current.tx) / vpRef.current.s;
          const iy = (ly - vpRef.current.ty) / vpRef.current.s;
          if (atFit) {
            const targetS = clampScale(fitScale * 2.2);
            const tx = lx - ix * targetS;
            const ty = ly - iy * targetS;
            animateTo({ tx, ty, s: targetS });
          } else {
            const tx = (containerDims.w - imgDims.w * fitScale) / 2;
            const ty = (containerDims.h - imgDims.h * fitScale) / 2;
            animateTo({ tx, ty, s: fitScale });
          }
          lastTap.current = { t: 0, x: 0, y: 0 };
          dragState.current = null;
          longPressFired.current = false;
          return;
        }
        lastTap.current = { t: now, x: lx, y: ly };
      }
      // ---------- Tap-without-drag in addMode (or asset placement) → drop pin ----------
      if ((addMode || placementActive) && !wasMoved && !fired && onCanvasClick) {
        const { lx, ly } = screenToImage(e.clientX, e.clientY);
        const ix = (lx - vp.tx) / vp.s;
        const iy = (ly - vp.ty) / vp.s;
        if (ix >= 0 && iy >= 0 && ix <= imgDims.w && iy <= imgDims.h) {
          // Iter EXP-1 — haptic confirmation for pin / I'm-Here / anchor.
          try { if (navigator.vibrate) navigator.vibrate(20); } catch { /* no-op */ }
          onCanvasClick(ix / imgDims.w, iy / imgDims.h);
        }
      }
      // ---------- Inertia & swipe gesture detection ----------
      if (wasMoved && velSamples.current.length >= 2) {
        const samples = velSamples.current;
        const last = samples[samples.length - 1];
        const prev = samples[0];
        const dt = Math.max(1, last.t - prev.t);
        const vx = (last.x - prev.x) / dt; // px per ms
        const vy = (last.y - prev.y) / dt;
        const speed = Math.hypot(vx, vy);
        // Field-mode horizontal swipe → sheet flip (only when not zoomed in).
        const HORIZ_DOM = Math.abs(vx) > Math.abs(vy) * 1.6;
        const FAST = speed > 0.6; // ≈ 600 px/sec
        const NEAR_FIT = vpRef.current.s <= fitScale * 1.08;
        if (onSwipe && wasMoved && HORIZ_DOM && FAST && NEAR_FIT) {
          onSwipe(vx > 0 ? "right" : "left");
          velSamples.current = [];
          dragState.current = null;
          longPressFired.current = false;
          return;
        }
        // If overscrolled, rubber-band snap back; cancel inertia (we don't
        // want momentum to fight the rebound).
        if (isOverscrolled()) {
          animateTo(snapTarget(), 320);
        } else if (speed > 0.05) {
          // Otherwise apply pan inertia.
          startInertia(vx, vy);
        }
      } else if (isOverscrolled()) {
        // Released without enough movement for inertia but still overscrolled
        // (e.g. quick tap-and-release at an edge). Snap back.
        animateTo(snapTarget(), 280);
      }
      dragState.current = null;
      longPressFired.current = false;
      velSamples.current = [];
    }
  };

  // Pan-inertia rAF loop: decay velocity, integrate into translate.
  const startInertia = useCallback((vx0, vy0) => {
    cancelInertia();
    let vx = vx0;
    let vy = vy0;
    let lastT = performance.now();
    const friction = 0.0042; // per-ms exponential decay
    const step = (now) => {
      const dt = now - lastT;
      lastT = now;
      vx *= Math.exp(-friction * dt);
      vy *= Math.exp(-friction * dt);
      // IP-002 · APPLY_ANIMATION_FRAME (pan inertia decay)
      setVp((prev) => ({
        ...prev,
        tx: constrainTx(prev.tx + vx * dt, prev.s),
        ty: constrainTy(prev.ty + vy * dt, prev.s),
      }), {
        type: "APPLY_ANIMATION_FRAME",
        source: "pan_inertia",
        user_initiated: false,
      });
      // Stop early if we hit a bound — let the rebound take over.
      if (isOverscrolled()) {
        inertiaRaf.current = 0;
        animateTo(snapTarget(), 280);
        return;
      }
      if (Math.hypot(vx, vy) > 0.02) {
        inertiaRaf.current = requestAnimationFrame(step);
      } else {
        inertiaRaf.current = 0;
      }
    };
    inertiaRaf.current = requestAnimationFrame(step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgDims, containerDims]);

  // Wheel = zoom (also handles trackpad-with-ctrl pinch).
  // IP-002: distinguish trackpad-pinch (ctrlKey synthetic) from wheel.
  const onWheel = (e) => {
    e.preventDefault();
    const { lx, ly } = screenToImage(e.clientX, e.clientY);
    // Smaller deltas for smoother trackpad zoom.
    const factor = Math.exp(-e.deltaY * 0.0015);
    zoomAroundPoint(vp.s * factor, lx, ly, e.ctrlKey ? "trackpad" : "wheel");
  };

  // Cleanup pending rAF on unmount.
  useEffect(() => () => {
    if (inertiaRaf.current) cancelAnimationFrame(inertiaRaf.current);
    if (animRaf.current) cancelAnimationFrame(animRaf.current);
    if (persistRaf.current) cancelAnimationFrame(persistRaf.current);
  }, []);

  // Imperative API for zoom buttons.
  const api = useMemo(() => ({
    zoomIn: () => zoomAroundPoint(vp.s * 1.25, containerDims.w / 2, containerDims.h / 2, "button"),
    zoomOut: () => zoomAroundPoint(vp.s / 1.25, containerDims.w / 2, containerDims.h / 2, "button"),
    fit: () => {
      // Iter 264 · testing-agent finding — a "Fit to screen" click that
      // lands right after a transient container reflow (e.g. a popover
      // menu closing) could capture a stale/undersized `containerDims`
      // from `fitScale`'s memo and apply it directly via the reducer's
      // authoritative-`next` path, which skips the normal FIT-case
      // safety clamp. Re-read the container's LIVE size from the DOM
      // at the moment of the click instead of trusting the memoized
      // value, so a one-frame-stale measurement can never stick.
      const liveEl = wrapRef.current;
      const liveDims = liveEl && liveEl.clientWidth > 0 && liveEl.clientHeight > 0
        ? { w: liveEl.clientWidth, h: liveEl.clientHeight }
        : containerDims;
      let s = computeFitScale(imgDims, liveDims);
      // Belt-and-braces: a fit scale under 5% of the memoized fitScale
      // (when that one is itself sane) is never legitimate — a real
      // sheet fitting a real viewport doesn't shrink 20x between renders.
      if (fitScale > 0 && s < fitScale * 0.2) s = fitScale;
      const tx = (liveDims.w - imgDims.w * s) / 2;
      const ty = (liveDims.h - imgDims.h * s) / 2;
      // IP-002 · RESET_VIEW
      setVp({ tx, ty, s }, { type: "RESET_VIEW", source: "user_reset", user_initiated: true });
    },
    // Iter 34 — pan to a normalized sheet point WITHOUT changing zoom.
    // Used by "Locate Me" (one-shot center) and "Follow Mode" (continuous
    // recentering as GPS updates). Preserving vp.s is the contract.
    centerOn: (nx, ny) => {
      const targetImgX = nx * imgDims.w;
      const targetImgY = ny * imgDims.h;
      const tx = containerDims.w / 2 - targetImgX * vp.s;
      const ty = containerDims.h / 2 - targetImgY * vp.s;
      // IP-002 · FOCUS_OBJECT (preserves scale — vp.s unchanged)
      setVp({ tx, ty, s: vp.s }, {
        type: "FOCUS_OBJECT", source: "center_on", user_initiated: true,
      });
    },
  }), [vp.s, containerDims, fitScale, imgDims, zoomAroundPoint, setVp]);

  // Expose API via a ref-like pattern through a custom event the parent listens to.
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.target !== sheetId) return;
      if (e.detail.action === "zoomIn") api.zoomIn();
      if (e.detail.action === "zoomOut") api.zoomOut();
      if (e.detail.action === "fit") api.fit();
      if (e.detail.action === "centerOn" && e.detail.nx != null && e.detail.ny != null) {
        api.centerOn(e.detail.nx, e.detail.ny);
      }
    };
    window.addEventListener("fop_canvas_cmd", handler);
    return () => window.removeEventListener("fop_canvas_cmd", handler);
  }, [api, sheetId]);

  return (
    <div
      ref={wrapRef}
      data-testid={testid}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      // Iter FIELD-UX-2 — iPadOS Safari interaction lockdown.
      // Without these, a long-press inside the drawing canvas pops the
      // native Share / Copy / Show Text callout menu and the user can't
      // tell whether their tool registered. Belt-and-suspenders: both
      // the React handler (synthetic contextmenu, fires on right-click +
      // long-press on most engines) AND the inline -webkit-* styles
      // (the only way to suppress the iOS Safari callout reliably).
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
      className={`relative overflow-hidden select-none touch-none ${
        addMode || placementActive || measureMode || cropMode || markupMode ? "cursor-crosshair" : (activeDragPin ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing")
      } ${className}`}
      style={{
        // PILLAR 4B — token-driven background so Daylight gets paper
        // and Field Black gets its original near-black. No hard-coded
        // hex means no theme bleed.
        background: "var(--fop-bg)",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        MsUserSelect: "none",
        userSelect: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      <div
        className="absolute top-0 left-0 will-change-transform"
        style={{
          transform: `translate(${vp.tx}px, ${vp.ty}px) scale(${vp.s})`,
          transformOrigin: "0 0",
          width: imgDims.w,
          height: imgDims.h,
        }}
        data-testid="sheet-canvas-stage"
        data-canvas-world="1"
        data-overscrolled={isOverscrolledOf(vp) ? "1" : "0"}
      >
        {/* Previous raster — fades out when a new sheet loads. */}
        {layers.prevUrl && layers.prevUrl !== layers.curUrl && (
          <img
            key={layers.prevUrl}
            src={layers.prevUrl}
            alt=""
            draggable={false}
            className="block absolute inset-0 pointer-events-none transition-opacity duration-200"
            style={{ width: imgDims.w, height: imgDims.h, opacity: layers.newReady ? 0 : 1 }}
          />
        )}
        <img
          ref={imgRef}
          key={layers.curUrl}
          src={layers.curUrl}
          alt=""
          onLoad={onNewImgLoad}
          draggable={false}
          decoding="async"
          fetchPriority="high"
          className="block transition-opacity duration-200"
          style={{
            width: imgDims.w,
            height: imgDims.h,
            opacity: layers.newReady ? 1 : 0,
            // Iter FIELD-UX-2 — iPadOS callout suppression on the image too.
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
            userSelect: "none",
            pointerEvents: "none",
          }}
        />
        {/* Tile pyramid — viewport-priority high-res overlay. Mounts inside
            the same transform so tiles inherit scale/translate and stay
            spatially locked vs pins / measurements / revision clouds.
            The base <img> above is always visible underneath — tiles only
            ADD sharper pixels, never gate visibility. */}
        {sheetId && imgDims.w > 100 && (
          <TileLayer
            sheetId={sheetId}
            revisionId={sheetRevision}
            viewport={vp}
            containerDims={containerDims}
          />
        )}
        {/* Operational overlays (Iter 31 P1) — layer markers, conflicts.
            Mount inside the transformed wrapper so SVG markers inherit
            pan/zoom and stay spatially locked through tile fade-ins. */}
        {sheetId && imgDims.w > 100 && (
          <LayerOverlay
            sheetId={sheetId}
            imgDims={imgDims}
            focusedId={focusedStructureId}
            onPick={onStructurePick}
          />
        )}
        {/* Pin layer — rendered inside the same transform so pins follow zoom/pan. */}
        <div className="absolute inset-0 pointer-events-none">
          {pins.map((p) => {
            // While a pin is being drag-relocated, render at the drag position
            // with a slight scale-up + shadow for tactile feedback.
            const isDragging = activeDragPin?.id === p.id;
            const x = isDragging ? activeDragPin.nx : (p.x || 0);
            const y = isDragging ? activeDragPin.ny : (p.y || 0);
            const lift = isDragging ? 1.25 : 1.0;
            return (
              <div
                key={p.id}
                data-pin-id={p.id}
                className={`absolute pointer-events-auto ${isDragging ? "z-20" : ""}`}
                style={{
                  left: `${x * 100}%`,
                  top: `${y * 100}%`,
                  transform: `translate(-50%, -50%) scale(${lift / vp.s})`,
                  filter: isDragging ? "drop-shadow(0 4px 8px rgba(0,0,0,0.55))" : undefined,
                  transition: isDragging ? "none" : "filter 120ms ease-out",
                }}
              >
                {renderPin ? renderPin(p) : null}
              </div>
            );
          })}
        </div>
        {/* Custom overlay slot (e.g. GPS location dot, calibration anchors,
            distance measure). Receives current view scale + img dims so layers
            can keep stroke widths constant under zoom. */}
        {overlay && (
          <div className="absolute inset-0 pointer-events-none" data-testid="sheet-canvas-overlay">
            {typeof overlay === "function" ? overlay({ scale: vp.s, imgDims }) : overlay}
          </div>
        )}
      </div>
    </div>
  );
}

/** Helper for parent components to drive zoom imperatively. */
export function dispatchCanvasCmd(targetId, action, args = {}) {
  window.dispatchEvent(new CustomEvent("fop_canvas_cmd", { detail: { target: targetId, action, ...args } }));
}
